import { useMemo } from "react";

import clsx from "clsx";

import { GenericTooltip } from "@components/tooltip-container";
import { useOverviewContext } from "@features/overview-v2/hooks";
import { type TileFilter, TileType } from "@features/overview-v2/types";
import {
  COMPARE_METRIC_1_BAR_CHART_PATTERN,
  COMPARE_METRIC_1_LINE_DASH,
} from "@features/sales-v3/utils/chart";
import { buildLegendImage } from "@features/sales-v3/utils/tooltip";
import {
  type BenchmarkIndicatorRow,
  UniversalBenchmarksIndicator,
  useUniversalBenchmarks,
} from "@features/universal-benchmarks";
import {
  CreativeMetricType,
  type GetOverviewMetricsReport_me_overviewMetricsReport,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";
import { MetricValue as BaseMetricValue } from "@shared/metric-value";
import Spinner from "@shared/spinner";

import { ChangePill } from "../../../../components/v2/change-pill/change-pill";

import { calculateMetric, getColorForIndexOrPLatform } from "./utils";

interface Props {
  variant: "single" | "multi";
  tileType: TileType;
  metric: SalesPageMetric;
  metricIndex: number;
  data: GetOverviewMetricsReport_me_overviewMetricsReport;
  compareData?: GetOverviewMetricsReport_me_overviewMetricsReport;
  filters: TileFilter[];
}

export const MetricValue = ({
  variant,
  tileType,
  metric,
  metricIndex,
  data,
  compareData,
  filters,
}: Props) => {
  const { filters: pageFilters } = useOverviewContext();

  const value = useMemo(() => {
    return calculateMetric(metric, data.rows);
  }, [metric, data]);

  const compareValue = useMemo(() => {
    if (!compareData) {
      return null;
    }

    return calculateMetric(metric, compareData.rows);
  }, [metric, compareData]);

  const benchmark = useUniversalBenchmarks({
    level: pageFilters.level,
    attributionModel: pageFilters.attributionModel,
    attributionWindow: pageFilters.attributionWindow,
    accountingMode: pageFilters.accountingMode,
    breakdowns: [],
    filters,
    metric: { id: metric.metadata.baseId, isLtv: metric.metadata.isLtv },
  });

  const benchmarkIndicatorRow = useMemo(() => {
    if (!benchmark || !metric || benchmark?.status === "loading") {
      return null;
    }

    return {
      name:
        metric.name ??
        metric.metadata.name ??
        metric.spec?.name ??
        metric.metadata.salesTableMetricId,
      current: value,
      target: benchmark.value,
      format: metric.format,
      positiveTrend: metric.isPositiveChangeGood ? "up" : "down",
    } as BenchmarkIndicatorRow;
  }, [benchmark, value, metric]);

  const changeValue = compareValue
    ? (value - compareValue) / compareValue
    : null;

  const labelColor = getColorForIndexOrPLatform(tileType, 0, {});
  const name =
    metric.metadata.overviewPageMetricName ||
    metric.metadata.name ||
    metric.name;

  const isBarChart = tileType === TileType.BAR_CHART;

  const legendCanvas = buildLegendImage({
    borderColor: labelColor,
    backgroundColor: COMPARE_METRIC_1_BAR_CHART_PATTERN,
    borderDash: COMPARE_METRIC_1_LINE_DASH,
    isBarChart,
    width: isBarChart ? 11 : 15,
    height: 11,
    borderWidth: 0,
  });

  return (
    <div>
      {variant === "multi" && (
        <div className="text-xs font-bold mb-1 flex items-center gap-1.5">
          {tileType !== TileType.METRIC && metricIndex === 0 && (
            <span
              className={clsx("shrink-0 inline-block rounded-sm", {
                "w-3 h-3": isBarChart,
                "w-4 h-[3px]": !isBarChart,
              })}
              style={{ backgroundColor: labelColor }}
            ></span>
          )}
          {tileType !== TileType.METRIC && metricIndex === 1 && (
            <div
              className={clsx("shrink-0 inline-block overflow-hidden", {
                "rounded-sm border": isBarChart,
              })}
              style={{ borderColor: labelColor }}
              dangerouslySetInnerHTML={{
                __html: `<img src="${legendCanvas.toDataURL("image/png")}" />`,
              }}
            />
          )}
          <span className="max-w-[140px] truncate inline-flex gap-0.5 items-center">
            {metric.composition?.type === CreativeMetricType.user_defined && (
              <GenericTooltip
                noInfoCircle
                content="Custom metric"
                className="truncate"
              >
                <i className="fa-regular fa-function text-cosmic" />
              </GenericTooltip>
            )}
            <GenericTooltip noInfoCircle content={name}>
              {name}
            </GenericTooltip>
          </span>
        </div>
      )}
      <div
        className={clsx("text-v2-action tracking-wide flex items-center", {
          "text-[42px] leading-10 mb-3": variant === "single",
          "text-lg leading-6": variant === "multi",
        })}
      >
        <div className="flex items-center">
          <BaseMetricValue
            as="span"
            className="mr-2.5"
            format={metric.format}
            value={value}
            precision="approximate"
          />
          {benchmark?.status === "loading" && (
            <div className="flex items-center mr-2">
              <Spinner colorClassname="w-3 h-3 text-cosmic text-xxs" />
            </div>
          )}
          {benchmarkIndicatorRow !== null && (
            <UniversalBenchmarksIndicator
              benchmarkIndicatorRows={[benchmarkIndicatorRow]}
            />
          )}
        </div>
        {changeValue != null && (
          <span className="mt-0">
            <ChangePill
              size={variant === "single" ? "lg" : "sm"}
              value={changeValue}
              isPositiveChangeGood={metric.isPositiveChangeGood}
            />
          </span>
        )}
      </div>
      {compareValue != null && (
        <div
          className={clsx("leading-6 text-v2-secondary font-light", {
            "text-lg": variant === "single",
            "text-xxs": variant === "multi",
          })}
        >
          Comparison period:{" "}
          <BaseMetricValue
            as="span"
            className="mr-2.5"
            format={metric.format}
            value={compareValue}
            precision="approximate"
          />
        </div>
      )}
    </div>
  );
};
